import React, { useCallback, useEffect, useRef } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { playKitchenAlertSound } from '../../shared/audio/kitchenAlertSound';
import { getCounterReadySoundEnabled } from '../../shared/localPrefs';
import { showSuccess } from '../../shared/ui/appToast';
import { fetchHospitalitySnapshot } from '../infra/hospitalityRepo';
import { useMesasLiveRefresh } from '../hooks/useMesasLiveRefresh';
import type { TableDraftSummary } from '../domain/hospitalityTypes';

type SignalFingerprint = {
  openSignalCount: number;
  hasCallWaiter: boolean;
  hasReadyToOrder: boolean;
  hasRequestBill: boolean;
};

function fingerprint(g: TableDraftSummary): SignalFingerprint {
  return {
    openSignalCount: g.openSignalCount,
    hasCallWaiter: g.hasCallWaiter,
    hasReadyToOrder: g.hasReadyToOrder,
    hasRequestBill: g.hasRequestBill,
  };
}

function kindLabel(prev: SignalFingerprint | undefined, next: SignalFingerprint): string | null {
  if (next.hasCallWaiter && !prev?.hasCallWaiter) return 'Te llaman';
  if (next.hasReadyToOrder && !prev?.hasReadyToOrder) return 'Ya pidieron';
  if (next.hasRequestBill && !prev?.hasRequestBill) return 'Piden la cuenta';
  if (next.openSignalCount > (prev?.openSignalCount ?? 0)) return 'Señal de mesa';
  return null;
}

/**
 * Toast + beep when a new open guest_signal appears (Mesas / POS).
 * Uses snapshot table_drafts flags; reuses kitchen sound pref.
 */
export function GuestSignalAlertProvider({ children }: { children: React.ReactNode }) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const hasTables = useHasBusinessCapability('tables');
  const canViewTables = useModulePermission('Tables', 'view');
  const canOperate = useModulePermission('Tables', 'operate');
  const shouldNotify = hasTables && (canViewTables || canOperate);

  const prevByTableRef = useRef<Map<string, SignalFingerprint>>(new Map());
  const primedRef = useRef(false);

  const syncSignals = useCallback(async () => {
    if (!branchId || !shouldNotify) return;
    const snap = await fetchHospitalitySnapshot(branchId, 30);
    const nextMap = new Map<string, SignalFingerprint>();

    for (const draft of snap.tableDrafts) {
      const fp = fingerprint(draft);
      nextMap.set(draft.diningTableId, fp);

      if (!primedRef.current) continue;

      const prev = prevByTableRef.current.get(draft.diningTableId);
      const label = kindLabel(prev, fp);
      if (!label) continue;

      const tableName = snap.diningTables.find((t) => t.id === draft.diningTableId)?.name?.trim();
      const mesaLabel = tableName
        ? /^mesa\b/i.test(tableName)
          ? tableName
          : `Mesa ${tableName}`
        : 'Mesa';
      showSuccess(label, mesaLabel);
      void getCounterReadySoundEnabled().then((v) => {
        if (v !== '0') playKitchenAlertSound();
      });
    }

    prevByTableRef.current = nextMap;
    primedRef.current = true;
  }, [branchId, shouldNotify]);

  useMesasLiveRefresh(branchId, () => void syncSignals(), {
    enabled: shouldNotify,
  });

  useEffect(() => {
    primedRef.current = false;
    prevByTableRef.current = new Map();
    void syncSignals();
  }, [syncSignals]);

  return <>{children}</>;
}
